import React, { useState } from 'react'
import { CopyOutlined } from '@ant-design/icons'
import { CurrencyFormatter } from 'helpers/formatters'
import {
  maskCardNumber,
  formatPanDisplay,
  formatVcnExpiration,
} from 'helpers/virtual-card'
import { normalizeVcnProvider } from 'modules/virtual-cards/normalizeVcnProvider'
import type { VcnDetailsTableProps } from './VcnPaymentManagement.types'
import {
  VcnDetailsContainer,
  VcnTable,
  VcnTableRow,
  VcnTableLabel,
  VcnTableValue,
  ShowHideButton,
  CopyableText,
  CopyIcon,
  StatusText,
} from './VcnPaymentManagement.styles'

const NOT_AVAILABLE = 'N/A'
const currencyFormatter = new CurrencyFormatter()

export function VcnDetailsTable({ virtualCard, onCopy }: VcnDetailsTableProps) {
  const [showCardNumber, setShowCardNumber] = useState(false)

  if (!virtualCard?.pan) {
    return null
  }

  const cardHolder =
    virtualCard.firstName && virtualCard.lastName
      ? `${virtualCard.firstName} ${virtualCard.lastName}`
      : (virtualCard.cardholderName ?? NOT_AVAILABLE)

  const cardNumberDisplay = showCardNumber
    ? formatPanDisplay(virtualCard.pan)
    : maskCardNumber(virtualCard.pan)

  const vcnProvider = normalizeVcnProvider(virtualCard.vcnProvider)

  const expirationDate = formatVcnExpiration(
    virtualCard.expiration_time,
    NOT_AVAILABLE
  )

  const monthlyLimit =
    virtualCard.monthly_card_limit != null
      ? virtualCard.monthly_card_limit === '-'
        ? '-'
        : currencyFormatter.format(Number(virtualCard.monthly_card_limit))
      : NOT_AVAILABLE

  const cardStatus = virtualCard.state
    ? String(virtualCard.state)
        .toLowerCase()
        .replace(/^\w/, (c) => c.toUpperCase())
    : NOT_AVAILABLE

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return NOT_AVAILABLE
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    } catch {
      return NOT_AVAILABLE
    }
  }

  const createdUpdated =
    virtualCard.created_time || virtualCard.last_modified_time
      ? `${formatDate(virtualCard.created_time)} - ${formatDate(virtualCard.last_modified_time)}`
      : NOT_AVAILABLE

  const tokenId =
    virtualCard.token ?? virtualCard.merchantLogId ?? NOT_AVAILABLE

  const handleCopy = (text: string | undefined) => {
    if (text && text !== NOT_AVAILABLE) {
      onCopy(text)
    }
  }

  const rows = [
    {
      label: 'Cardholder name:',
      value: cardHolder,
      copyable: cardHolder !== NOT_AVAILABLE,
    },
    {
      label: 'Card number:',
      value: (
        <>
          <CopyableText onClick={() => handleCopy(virtualCard.pan)}>
            {cardNumberDisplay}
          </CopyableText>
          {' · '}
          <ShowHideButton
            type="button"
            onClick={() => setShowCardNumber((s) => !s)}
          >
            {showCardNumber ? 'Hide' : 'Show'}
          </ShowHideButton>
        </>
      ),
      copyable: false, // Custom copy handling above
    },
    {
      label: 'VCN provider:',
      value: vcnProvider,
      copyable: false,
    },
    {
      label: 'Expiration date:',
      value: expirationDate,
      copyable: expirationDate !== NOT_AVAILABLE,
    },
    {
      label: 'CVV:',
      value: virtualCard.cvv_number ?? NOT_AVAILABLE,
      copyable: !!virtualCard.cvv_number,
    },
    {
      label: 'Monthly card limit:',
      value: monthlyLimit,
      copyable: monthlyLimit !== NOT_AVAILABLE && monthlyLimit !== '-',
      copyValue:
        virtualCard.monthly_card_limit != null &&
        virtualCard.monthly_card_limit !== '-'
          ? String(virtualCard.monthly_card_limit)
          : undefined,
    },
    {
      label: 'Card status:',
      value: <StatusText $status={virtualCard.state}>{cardStatus}</StatusText>,
      copyable: false,
    },
    {
      label: 'Created / Last updated:',
      value: createdUpdated,
      copyable: false,
    },
    {
      label: 'Token ID:',
      value: tokenId,
      copyable: tokenId !== NOT_AVAILABLE,
    },
  ]

  return (
    <VcnDetailsContainer>
      <VcnTable>
        {rows.map((row) => (
          <VcnTableRow key={row.label}>
            <VcnTableLabel>{row.label}</VcnTableLabel>
            <VcnTableValue>
              {row.copyable ? (
                <CopyableText
                  onClick={() =>
                    handleCopy(
                      row.copyValue ??
                        (typeof row.value === 'string' ? row.value : undefined)
                    )
                  }
                >
                  {row.value}
                  <CopyIcon>
                    <CopyOutlined />
                  </CopyIcon>
                </CopyableText>
              ) : (
                row.value
              )}
            </VcnTableValue>
          </VcnTableRow>
        ))}
      </VcnTable>
    </VcnDetailsContainer>
  )
}
